/**
 * FilosofiCard — profil socio-démographique du quartier (carreau 200 m INSEE).
 *
 * UX :
 *   - Header avec titre + sous-titre "X hab · Y ménages · maille 200 m".
 *   - Pas de jugement coloré dans la card : Filosofi décrit le quartier,
 *     ce n'est pas en soi un red flag. La card pose le contexte ; Claude
 *     décide dans les red flags si une valeur extrême mérite un warn.
 *   - 3 KPI principaux en grille (niveau de vie / pauvreté / propriétaires)
 *     avec un mini-chip de comparaison vs. France métropolitaine.
 *   - Section démographie : barre empilée < 18 / 18-64 / 65+.
 *   - Section logement : maisons vs. appart, ancienneté, % social.
 *   - Bandeau d'avertissement quand `estimated=true` (secret stat INSEE).
 */

import type { FicheDoc } from "../../lib/fiches";
import {
  useFilosofi,
  FRANCE_REF,
  type FilosofiData,
} from "../../lib/env/filosofi";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

export function FilosofiCard({ fiche }: Props) {
  const state = useFilosofi(fiche);
  const data = state.status === "ready" ? state.data : null;
  const subtitle = data ? summarise(data) : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Users size={18} />}
      title="Profil du quartier (Filosofi)"
      subtitle={subtitle}
      state={state}
      footer={
        <>
          Source : INSEE Filosofi via Géoplateforme. Données fiscales
          agrégées par carreaux de 200 m (1 km en zone rurale).
          {data?.estimated && (
            <>
              {" "}
              Valeurs <strong>estimées</strong> par modélisation (secret
              statistique INSEE) — fiabilité réduite sur cette zone.
            </>
          )}
        </>
      }
    >
      {state.status === "ready" && state.data === null ? (
        <EmptyArea />
      ) : (
        state.status === "ready" && data && <FilosofiBody data={data} />
      )}
    </EnvCardShell>
  );
}

// ─── Empty area ──────────────────────────────────────────────────────────

function EmptyArea() {
  return (
    <div className="rounded-lg bg-ink-50 border border-ink-100 px-3.5 py-3">
      <div className="text-[12.5px] text-ink-700">
        Aucun carreau Filosofi peuplé sous la parcelle — zone probablement
        non résidentielle (forêt, parc, ZAC, …).
      </div>
    </div>
  );
}

// ─── Body ────────────────────────────────────────────────────────────────

function FilosofiBody({ data }: { data: FilosofiData }) {
  return (
    <div className="space-y-4">
      <KpiRow data={data} />
      <DemographieSection data={data} />
      <LogementSection data={data} />
    </div>
  );
}

// 3 KPI principaux : niveau de vie / pauvreté / propriétaires.
function KpiRow({ data }: { data: FilosofiData }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <Kpi
        label="Niveau de vie"
        value={
          data.niveauDeVieMoyen != null
            ? `${formatEur(data.niveauDeVieMoyen)}`
            : "—"
        }
        hint={
          data.niveauDeVieMoyen != null
            ? compareToRef(data.niveauDeVieMoyen, FRANCE_REF.niveauDeVieMoyen, "€/an", true)
            : undefined
        }
      />
      <Kpi
        label="Taux de pauvreté"
        value={
          data.tauxPauvrete != null
            ? `${data.tauxPauvrete.toFixed(1).replace(".", ",")} %`
            : "—"
        }
        hint={
          data.tauxPauvrete != null
            ? compareToRef(data.tauxPauvrete, FRANCE_REF.tauxPauvrete, "pts")
            : undefined
        }
      />
      <Kpi
        label="Propriétaires"
        value={
          data.partProprietaires != null
            ? `${data.partProprietaires.toFixed(1).replace(".", ",")} %`
            : "—"
        }
        hint={
          data.partProprietaires != null
            ? compareToRef(data.partProprietaires, FRANCE_REF.partProprietaires, "pts")
            : undefined
        }
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl bg-ink-50 hairline px-3 py-2.5">
      <div className="text-[10.5px] uppercase tracking-[0.06em] text-ink-500">
        {label}
      </div>
      <div className="text-[18px] font-semibold tabular text-ink-900 leading-tight mt-0.5">
        {value}
      </div>
      {hint && (
        <div className="text-[10.5px] text-ink-500 mt-0.5 leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}

// Section démographie — barre empilée < 18 / 18-64 / 65+.
function DemographieSection({ data }: { data: FilosofiData }) {
  const u18 = data.partMoins18 ?? 0;
  const o65 = data.part65plus ?? 0;
  const middle = Math.max(0, 100 - u18 - o65);
  return (
    <section>
      <SectionHeader title="Démographie" />
      <div className="flex h-2 rounded-full overflow-hidden bg-ink-100">
        <div
          className="bg-sky-400"
          style={{ width: `${u18}%` }}
          title={`< 18 ans : ${u18.toFixed(1)} %`}
        />
        <div
          className="bg-ink-400"
          style={{ width: `${middle}%` }}
          title={`18-64 ans : ${middle.toFixed(1)} %`}
        />
        <div
          className="bg-amber-400"
          style={{ width: `${o65}%` }}
          title={`65+ ans : ${o65.toFixed(1)} %`}
        />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <Stat
          dot="bg-sky-400"
          label="< 18 ans"
          value={fmtPct(data.partMoins18)}
          compare={`Fr ${FRANCE_REF.partMoins18}%`}
        />
        <Stat
          dot="bg-ink-400"
          label="18-64 ans"
          value={`${middle.toFixed(1).replace(".", ",")} %`}
        />
        <Stat
          dot="bg-amber-400"
          label="65+ ans"
          value={fmtPct(data.part65plus)}
          compare={`Fr ${FRANCE_REF.part65plus}%`}
        />
      </div>
    </section>
  );
}

// Section logement — composition + ancienneté + social.
function LogementSection({ data }: { data: FilosofiData }) {
  return (
    <section>
      <SectionHeader title="Parc de logements" />
      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="Maisons"
          value={fmtPct(data.partMaisons)}
          compare={`Fr ${FRANCE_REF.partMaisons}%`}
        />
        <Stat
          label="Logements sociaux"
          value={fmtPct(data.partLogementsSociaux)}
        />
        <Stat
          label="Construits avant 1945"
          value={fmtPct(data.partAvant1945)}
        />
        <Stat
          label="Construits après 1990"
          value={fmtPct(data.partApres1990)}
        />
        {data.partMonoparentales != null && (
          <Stat
            label="Familles monoparentales"
            value={fmtPct(data.partMonoparentales)}
          />
        )}
        {data.surfaceMoyenne != null && (
          <Stat
            label="Surface moyenne"
            value={`${data.surfaceMoyenne.toFixed(0)} m²`}
          />
        )}
      </div>
    </section>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.08em] text-ink-500 mb-1.5">
      {title}
    </div>
  );
}

// `compare` plutôt que `ref` : `ref` est un nom de prop réservé par
// React (forwarding/imperative handle), l'utiliser comme prop normale
// déclenche l'erreur #290 en build minifié.
function Stat({
  dot,
  label,
  value,
  compare,
}: {
  dot?: string;
  label: string;
  value: string;
  compare?: string;
}) {
  return (
    <div className="flex items-baseline gap-2 text-[12.5px]">
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot} mt-1`} />}
      <span className="text-ink-700 flex-1 truncate">{label}</span>
      <span className="font-semibold text-ink-900 tabular shrink-0">
        {value}
      </span>
      {compare && (
        <span className="text-[10.5px] text-ink-400 shrink-0 tabular">
          ({compare})
        </span>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function summarise(d: FilosofiData): string {
  const ind = Math.round(d.individus);
  const men = Math.round(d.menages);
  const scaleLabel = d.scale === "200m" ? "200 m" : "1 km";
  return `${ind} hab. · ${men} mén. · maille ${scaleLabel}${d.estimated ? " (estimé)" : ""}`;
}

function formatEur(v: number): string {
  // €/an, formaté à l'européenne avec espace fine et pas de décimale.
  return `${Math.round(v).toLocaleString("fr-FR")} €`;
}

function fmtPct(v: number | null): string {
  if (v == null) return "—";
  return `${v.toFixed(1).replace(".", ",")} %`;
}

// Compare une valeur à la référence France et retourne un libellé court
// "× Fr" ou "+/- N pts". On garde du ton neutre (pas de couleur) pour
// laisser à Claude la responsabilité de qualifier en red flag.
function compareToRef(
  value: number,
  ref: number,
  unit: "€/an" | "pts",
  ratio = false,
): string {
  if (ratio) {
    const r = value / ref;
    if (r >= 1.5) return `× ${r.toFixed(1)} Fr`;
    if (r >= 1.1) return `+${Math.round((r - 1) * 100)} % vs. Fr`;
    if (r >= 0.9) return `comparable à Fr`;
    if (r >= 0.7) return `−${Math.round((1 - r) * 100)} % vs. Fr`;
    return `× ${r.toFixed(1)} Fr (très inférieur)`;
  }
  // Différence en points
  const diff = value - ref;
  const sign = diff >= 0 ? "+" : "−";
  const absDiff = Math.abs(diff);
  if (absDiff < 1) return `≈ Fr`;
  return `${sign}${absDiff.toFixed(0)} ${unit} vs. Fr`;
}
